/**
 * 0.49.86 - Process timeline: numbered steps on a vertical line.
 *
 * Henaket "Process Timeline" (Figma, desktop and mobile), with what the
 * live Henaket sites do where Figma says nothing (the line's color and
 * width, the label's color). Each step: a dot on the line, a label
 * ("Step 1", from a pattern), a heading and a body in Markdown.
 *
 * Element fields (all optional but the steps):
 *   step_count            1..15, default 3
 *   step_label            label pattern, {n} = the step's number (Step {n})
 *   step_N_label          this step's own label instead of the pattern
 *   step_N_title          heading
 *   step_N_text           body, Markdown (paragraphs, lists, links)
 *   pt_title_level        'h2'..'h5' (h2): the heading's level and size
 *   pt_spacing            'figma' (default) | 'compact' (as the live sites)
 *   pt_end_dot            false hides the dot under the last step
 *   pt_line_width         2 | 4 (4)
 *   pt_dot_color          (Green 900)
 *   pt_label_color        (Green 1000)
 *   pt_line_color         (Blue 200)
 *   pt_title_color        (Text 800)
 *   pt_text_color         (Text 700)
 *
 * A step whose heading and body are both empty (after {field} and
 * =formula) is left out, and the numbers count the steps shown, so a
 * formula can drop a step that does not apply.
 *
 * `resolve(text)` gives a text's shown value: the page resolves {field}
 * and =formula, the editor's canvas shows the text as typed.
 */
import MarkdownContent from './MarkdownContent';
import { colorCss } from '../lib/colorCss';
import styles from './ProcessTimeline.module.css';

export const PT_MAX_STEPS = 15;

export function ptStepCount(e) {
  const c = parseInt(e && e.step_count, 10);
  return Number.isFinite(c) ? Math.min(Math.max(c, 1), PT_MAX_STEPS) : 3;
}

export default function ProcessTimeline({ e, resolve = (t) => t }) {
  const r = (v) => {
    if (v === undefined || v === null || v === '') return '';
    const out = resolve(String(v));
    return out === undefined || out === null ? '' : String(out);
  };
  const steps = [];
  for (let i = 1; i <= ptStepCount(e); i++) {
    const title = r(e[`step_${i}_title`]).trim();
    const text = r(e[`step_${i}_text`]);
    if (!title && !text.trim()) continue;
    steps.push({ i, title, text, ownLabel: e[`step_${i}_label`] });
  }
  if (steps.length === 0) return null;

  const level = /^h[2-5]$/.test(e.pt_title_level || '') ? e.pt_title_level : 'h2';
  const Heading = level;
  const endDot = e.pt_end_dot !== false;
  const style = {};
  const setColor = (name, value) => { const c = colorCss(value); if (c) style[name] = c; };
  setColor('--pt-dot', e.pt_dot_color);
  setColor('--pt-label', e.pt_label_color);
  setColor('--pt-line', e.pt_line_color);
  setColor('--pt-title', e.pt_title_color);
  setColor('--pt-text', e.pt_text_color);
  if (String(e.pt_line_width) === '2') style['--pt-line-w'] = '2px';
  const textColor = colorCss(e.pt_text_color) || 'var(--color-text-700)';

  const cls = [styles.timeline, e.pt_spacing === 'compact' ? styles.compact : styles.figma,
    endDot ? styles.withEnd : ''].filter(Boolean).join(' ');
  const pattern = e.step_label === undefined || e.step_label === null ? 'Step {n}' : String(e.step_label);

  return (
    <ol className={cls} style={style}>
      {steps.map((s, k) => {
        const n = k + 1;
        const label = r(s.ownLabel ? s.ownLabel : pattern.split('{n}').join(String(n))).trim();
        const last = k === steps.length - 1;
        return (
          <li key={s.i} className={`${styles.step} ${last ? styles.last : ''}`}>
            <span className={styles.dot} aria-hidden="true" />
            {(!last || endDot) && <span className={styles.line} aria-hidden="true" />}
            <div className={styles.content}>
              {label && <div className={styles.label}>{label}</div>}
              {s.title && <Heading className={`${styles.title} ${styles[level]}`}>{s.title}</Heading>}
              {s.text.trim() && (
                <div className={styles.body}>
                  <MarkdownContent source={s.text} color={textColor} />
                </div>
              )}
            </div>
            {last && endDot && <span className={`${styles.dot} ${styles.endDot}`} aria-hidden="true" />}
          </li>
        );
      })}
    </ol>
  );
}
